/**
 * Login Component
 * Provides secure login form for customs brokers
 * Includes rate limiting, validation, and error handling
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  Text,
  Input,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  FormHelperText,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Heading,
  Icon,
  Image,
  Link,
  InputGroup,
  InputRightElement,
  IconButton,
  useToast,
} from '@chakra-ui/react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { rateLimiter, isValidEmail, formatTimeRemaining } from '../utils/security.utils';

interface LoginProps {}

const Login: React.FC<LoginProps> = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { login, isLoading, error, clearError } = useAuth();
  const { t } = useLanguage();

  // Form state
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  
  // Rate limiting state
  const [isRateLimited, setIsRateLimited] = useState(false);
  const [timeUntilUnblock, setTimeUntilUnblock] = useState(0);
  const [remainingAttempts, setRemainingAttempts] = useState(5);

  /**
   * Check rate limit status on mount and when email changes
   */
  useEffect(() => {
    if (email) {
      updateRateLimitStatus();
    }
  }, [email]);

  /**
   * Update rate limit status
   */
  const updateRateLimitStatus = () => {
    const allowed = rateLimiter.isAllowed(email);
    const remaining = rateLimiter.getRemainingAttempts(email);
    const timeUntil = rateLimiter.getTimeUntilUnblocked(email);
    
    setIsRateLimited(!allowed);
    setRemainingAttempts(remaining);
    setTimeUntilUnblock(timeUntil);
  };

  /**
   * Countdown timer for rate limit
   */
  useEffect(() => {
    if (timeUntilUnblock > 0) {
      const timer = setInterval(() => {
        setTimeUntilUnblock(prev => {
          const newTime = Math.max(0, prev - 1000);
          if (newTime === 0) {
            setIsRateLimited(false);
            updateRateLimitStatus();
          }
          return newTime;
        });
      }, 1000);
      
      return () => clearInterval(timer);
    }
  }, [timeUntilUnblock]);

  /**
   * Clear errors when user starts typing
   */
  useEffect(() => {
    if (email || password) {
      setErrors({});
      clearError();
    }
  }, [email, password, clearError]);

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: { email?: string; password?: string } = {};

    // Email validation
    if (!email.trim()) {
      newErrors.email = t('login.email_required');
    } else if (!isValidEmail(email)) {
      newErrors.email = t('login.email_invalid');
    }

    // Password validation
    if (!password) {
      newErrors.password = t('login.password_required');
    } else if (password.length < 8) {
      newErrors.password = t('login.password_min_length');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle login form submission
   */
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    if (!validateForm()) {
      return;
    }

    // Check rate limiting
    if (!rateLimiter.isAllowed(email)) {
      const timeUntil = rateLimiter.getTimeUntilUnblocked(email);
      toast({
        title: t('login.locked'),
        description: t('login.locked_desc', { time: formatTimeRemaining(timeUntil) }),
        status: 'warning',
        duration: 5000,
        isClosable: true,
      });
      updateRateLimitStatus();
      return;
    }

    try {
      const response = await login(email, password);

      if (response.success) {
        // Reset rate limiter on success
        rateLimiter.reset(email);
        
        toast({
          title: t('login.success_title'),
          description: t('login.success_desc'),
          status: 'success',
          duration: 2000,
          isClosable: true,
        });

        // Redirect to dashboard
        setTimeout(() => {
          navigate('/bill-of-lading');
        }, 500);
      } else {
        // Record failed attempt
        rateLimiter.recordAttempt(email);
        updateRateLimitStatus();
        
        // Show error message
        toast({
          title: t('login.failed_title'),
          description: response.error || t('login.failed_desc'),
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
        
        // Show remaining attempts warning
        const remaining = rateLimiter.getRemainingAttempts(email);
        if (remaining > 0 && remaining <= 2) {
          toast({
            title: t('common.warning'),
            description: t('login.attempts_left', { count: remaining }),
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }
    } catch (err: any) {
      toast({
        title: t('common.error'),
        description: err.message || t('common.error'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  /**
   * Handle Enter key press
   */
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading && !isRateLimited) {
      handleLogin(e as any);
    }
  };

  return (
    <Box
      minH="100vh"
      bg="appleBg"
      display="flex"
      alignItems="center"
      justifyContent="center"
      px={4}
    >
      <Box maxW="360px" w="full" py={20}>
        <form onSubmit={handleLogin}>
          <VStack spacing={10}>
            {/* Header */}
            <VStack spacing={4} textAlign="center">
              <Image src="/BRL_Blue_Squared.png" alt="Logo" boxSize={10} />
              <Heading fontSize="28px" fontWeight="600" lineHeight="1.14" color="appleTextPrimary">
                {t('login.title')}
              </Heading>
            </VStack>

            {/* Status Alert — rate limit takes priority over the attempts warning */}
            {isRateLimited ? (
              <Alert status="warning" variant="subtle" borderRadius="sm" w="full">
                <AlertIcon />
                <Box>
                  <AlertTitle>{t('login.locked')}</AlertTitle>
                  <AlertDescription>
                    {t('login.locked_desc', { time: formatTimeRemaining(timeUntilUnblock) })}
                  </AlertDescription>
                </Box>
              </Alert>
            ) : remainingAttempts <= 2 && remainingAttempts > 0 ? (
              <Alert status="warning" variant="subtle" borderRadius="sm" w="full">
                <AlertIcon />
                <AlertDescription>
                  {t('login.attempts_left', { count: remainingAttempts })}
                </AlertDescription>
              </Alert>
            ) : null}

            {/* Form Fields */}
            <VStack spacing={6} w="full">
              {/* Email */}
              <FormControl isInvalid={!!errors.email}>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('login.email_label')}
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="flushed"
                    type="email"
                    placeholder={t('login.email_placeholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value.toLowerCase())}
                    onKeyPress={handleKeyPress}
                    size="lg"
                    autoComplete="email"
                    aria-label="Email address"
                    focusBorderColor="appleAccent"
                    borderColor="appleCardBorder"
                  />
                </InputGroup>
                <FormHelperText fontSize="xs">
                  {t('login.email_helper')}
                </FormHelperText>
                <FormErrorMessage>{errors.email}</FormErrorMessage>
              </FormControl>

              {/* Password */}
              <FormControl isInvalid={!!errors.password}>
                <FormLabel fontSize="xs" fontWeight="medium" color="appleTextSecondary">
                  {t('login.password_label')}
                </FormLabel>
                <InputGroup>
                  <Input
                    variant="flushed"
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('login.password_placeholder')}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyPress={handleKeyPress}
                    size="lg"
                    autoComplete="current-password"
                    aria-label="Password"
                    focusBorderColor="appleAccent"
                    borderColor="appleCardBorder"
                  />
                  <InputRightElement h="full">
                    <IconButton
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      icon={<Icon as={showPassword ? FaEyeSlash : FaEye} />}
                      onClick={() => setShowPassword(!showPassword)}
                      variant="ghost"
                      size="sm"
                    />
                  </InputRightElement>
                </InputGroup>
                <FormErrorMessage>{errors.password}</FormErrorMessage>
              </FormControl>
            </VStack>

            {/* Forgot Password Link */}
            <Box w="full" mt={-4} textAlign="right">
              <Link
                as={RouterLink}
                to="/forgot-password"
                fontSize="xs"
                color="appleAccent"
              >
                {t('login.forgot_password')}
              </Link>
            </Box>

            {/* Login Button */}
            <Button
              type="submit"
              colorScheme="blue"
              size="lg"
              w="full"
              borderRadius="full"
              isLoading={isLoading}
              isDisabled={isRateLimited}
              loadingText={t('login.signing_in')}
            >
              {t('login.sign_in')}
            </Button>

            {/* Register Link */}
            <VStack spacing={3} pt={4}>
              <Text fontSize="sm" color="appleTextSecondary">
                {t('login.first_time')}
              </Text>
              <Link
                as={RouterLink}
                to="/register"
                fontSize="sm"
                fontWeight="medium"
                color="appleAccent"
              >
                {t('login.create_account')}
              </Link>
            </VStack>
          </VStack>
        </form>
      </Box>
    </Box>
  );
};

export default Login;
